import { worldResource } from '@/world';
import {
  Item,
  ShopBuyClientPacket,
  type ShopCraftItem,
  ShopCreateClientPacket,
  ShopSellClientPacket,
  type ShopSoldItem,
  type ShopTradeItem,
} from 'eolib';

import type { Client } from '@/client';
import { GOLD_ITEM_ID } from '@/consts';
import { EOResourceID } from '@/edf';
import { SfxId } from '@/sfx';
import { ChatIcon } from '@/ui/enums';

type OpenedSubscriber = (
  name: string,
  tradeItems: ShopTradeItem[],
  craftItems: ShopCraftItem[],
) => void;
type ChangedSubscriber = () => void;

type ExtendedCraftIngredient = {
  item_id?: number;
  itemId?: number;
  amount?: number;
};

type ExtendedCraftRecipe = {
  item_id?: number;
  itemId?: number;
  gold_cost?: number;
  goldCost?: number;
  ingredients?: ExtendedCraftIngredient[];
};

type ExtendedCraftShop = {
  behavior_id?: number;
  behaviorId?: number;
  crafts?: ExtendedCraftRecipe[];
};

export class ShopController {
  private client: Client;
  shopName = '';
  tradeItems: ShopTradeItem[] = [];
  craftItems: ShopCraftItem[] = [];
  pendingShopBehaviorId = 0;
  shopBehaviorId = 0;
  private authoritativeShop = false;
  private openGeneration = 0;

  private extendedCrafts: ExtendedCraftShop[] = [];
  private extendedCraftsPromise: Promise<void> | null = null;

  private openedSubscribers: OpenedSubscriber[] = [];
  private changedSubscribers: ChangedSubscriber[] = [];

  constructor(client: Client) {
    this.client = client;
  }

  subscribeOpened(cb: OpenedSubscriber): void {
    this.openedSubscribers.push(cb);
  }

  unsubscribeOpened(cb: OpenedSubscriber): void {
    this.openedSubscribers = this.openedSubscribers.filter((s) => s !== cb);
  }

  subscribeChanged(cb: ChangedSubscriber): void {
    this.changedSubscribers.push(cb);
  }

  unsubscribeChanged(cb: ChangedSubscriber): void {
    this.changedSubscribers = this.changedSubscribers.filter((s) => s !== cb);
  }

  handleOpened(
    sessionId: number,
    shopName: string,
    tradeItems: ShopTradeItem[],
    craftItems: ShopCraftItem[],
  ): void {
    this.authoritativeShop = false;
    const generation = ++this.openGeneration;
    this.client.sessionId = sessionId;
    this.shopBehaviorId = this.pendingShopBehaviorId;
    this.shopName = shopName;
    this.tradeItems = tradeItems;
    this.craftItems = this.applyExtendedCrafts(craftItems);

    for (const cb of this.openedSubscribers)
      cb(shopName, tradeItems, this.craftItems);

    this.loadExtendedCrafts().then(() => {
      if (this.authoritativeShop || generation !== this.openGeneration) return;
      const updatedCraftItems = this.applyExtendedCrafts(this.craftItems);
      if (updatedCraftItems !== this.craftItems) {
        this.craftItems = updatedCraftItems;
        for (const cb of this.changedSubscribers) cb();
      }
    });
  }

  handleSnapshot(value: unknown): void {
    const data = value as {version?: number; sessionId?: number; behaviorId?: number; shopName?: string; tradeItems?: ShopTradeItem[]; crafts?: ExtendedCraftRecipe[]};
    if (!data || data.version !== 1 || data.sessionId !== this.client.sessionId || !Number.isSafeInteger(data.behaviorId) || data.behaviorId! <= 0 || !Array.isArray(data.tradeItems) || !Array.isArray(data.crafts) || typeof data.shopName !== 'string') return;
    const valid = (n: unknown) => Number.isSafeInteger(n) && Number(n) >= 0;
    if (!data.tradeItems.every(t=>valid(t.itemId)&&valid(t.buyPrice)&&valid(t.sellPrice)&&valid(t.maxBuyAmount))) return;
    if (!data.crafts.every(r=>valid(r.item_id)&&valid(r.gold_cost)&&Array.isArray(r.ingredients)&&r.ingredients.every(i=>valid(i.item_id)&&valid(i.amount)))) return;
    this.authoritativeShop = true;
    // The session-authenticated server snapshot, not a cached NPC record,
    // identifies the shop (especially on a first visit after a map update).
    this.shopBehaviorId = data.behaviorId!;
    this.shopName = data.shopName;
    this.tradeItems = data.tradeItems;
    this.craftItems = data.crafts.map(r=>this.toCraftItem(r));
    // A full snapshot updates an already-open shop; preserve its selected tab.
    for (const cb of this.changedSubscribers) cb();
  }

  private async loadExtendedCrafts(): Promise<void> {
    if (!this.extendedCraftsPromise) {
      this.extendedCraftsPromise = (async () => {
        try {
          const response = await fetch(worldResource('/data/extended-crafts.json'), {
            cache: 'no-store',
          });
          if (!response.ok) return;
          const data = await response.json();
          this.extendedCrafts = Array.isArray(data?.shops) ? data.shops : [];
        } catch {
          this.extendedCrafts = [];
        }
      })();
    }
    await this.extendedCraftsPromise;
  }

  private extendedBehaviorId(shop: ExtendedCraftShop): number {
    return Number(shop.behavior_id ?? shop.behaviorId ?? 0);
  }

  private extendedItemId(entry: ExtendedCraftRecipe | ExtendedCraftIngredient): number {
    return Number(entry.item_id ?? entry.itemId ?? 0);
  }

  private extendedGoldCost(recipe: ExtendedCraftRecipe): number {
    return Number(recipe.gold_cost ?? recipe.goldCost ?? 0);
  }

  private toIngredient(itemId: number, amount: number): Item {
    const item = new Item();
    item.id = itemId;
    item.amount = amount;
    return item;
  }

  private toCraftItem(recipe: ExtendedCraftRecipe): ShopCraftItem {
    const ingredients: Item[] = [];
    const goldCost = this.extendedGoldCost(recipe);
    if (goldCost > 0) ingredients.push(this.toIngredient(GOLD_ITEM_ID, goldCost));

    for (const ingredient of recipe.ingredients ?? []) {
      const itemId = this.extendedItemId(ingredient);
      const amount = Number(ingredient.amount ?? 0);
      if (itemId > 0 && amount > 0) ingredients.push(this.toIngredient(itemId, amount));
    }

    return {
      itemId: this.extendedItemId(recipe),
      ingredients,
    } as unknown as ShopCraftItem;
  }

  private applyExtendedCrafts(craftItems: ShopCraftItem[]): ShopCraftItem[] {
    if (!this.shopBehaviorId || this.extendedCrafts.length === 0) return craftItems;
    const shop = this.extendedCrafts.find(
      (entry) => this.extendedBehaviorId(entry) === this.shopBehaviorId,
    );
    if (!shop?.crafts?.length) return craftItems;

    const byItemId = new Map<number, ExtendedCraftRecipe>();
    for (const recipe of shop.crafts) {
      const itemId = this.extendedItemId(recipe);
      if (itemId > 0) byItemId.set(itemId, recipe);
    }

    let changed = false;
    const result = craftItems.map((craftItem) => {
      const recipe = byItemId.get(craftItem.itemId);
      if (!recipe) return craftItem;
      changed = true;
      byItemId.delete(craftItem.itemId);
      return this.toCraftItem(recipe);
    });

    for (const recipe of byItemId.values()) {
      result.push(this.toCraftItem(recipe));
      changed = true;
    }

    return changed ? result : craftItems;
  }

  notifyBought(
    goldAmount: number,
    boughtItem: Item,
    weightCurrent: number,
  ): void {
    this.client.inventoryController.setItem(GOLD_ITEM_ID, goldAmount);
    this.client.inventoryController.addItem(boughtItem.id, boughtItem.amount);
    this.client.weight.current = weightCurrent;
    this.client.audioController.playById(SfxId.BuySell);
    for (const cb of this.changedSubscribers) cb();

    const name = this.client.getEifRecordById(boughtItem.id)?.name ?? '';
    const msg = this.client.locale.shop.boughtMsg
      .replace('{amount}', String(boughtItem.amount))
      .replace('{name}', name);
    this.client.toastController.show(msg);
    this.client.chatController.notifyServerChat({
      message: msg,
      icon: ChatIcon.Star,
    });
  }

  notifySold(
    soldItem: ShopSoldItem,
    goldAmount: number,
    weightCurrent: number,
  ): void {
    this.client.inventoryController.setItem(GOLD_ITEM_ID, goldAmount);

    const prevAmount = this.client.inventoryController.getItemAmount(
      soldItem.id,
    );
    this.client.inventoryController.setItem(soldItem.id, soldItem.amount);
    this.client.weight.current = weightCurrent;
    this.client.audioController.playById(SfxId.BuySell);
    for (const cb of this.changedSubscribers) cb();

    const soldAmount = prevAmount - (soldItem.amount ?? 0);
    const name = this.client.getEifRecordById(soldItem.id)?.name ?? '';
    const msg = this.client.locale.shop.soldMsg
      .replace('{amount}', String(soldAmount > 0 ? soldAmount : 1))
      .replace('{name}', name);
    this.client.toastController.show(msg);
    this.client.chatController.notifyServerChat({
      message: msg,
      icon: ChatIcon.DownArrow,
    });
  }

  notifyCrafted(
    craftItemId: number,
    ingredients: Item[],
    weightCurrent: number,
  ): void {
    for (const ingredient of ingredients) {
      if (!ingredient.id) continue;
      this.client.inventoryController.setItem(ingredient.id, ingredient.amount);
    }

    this.client.inventoryController.addItem(craftItemId);
    this.client.weight.current = weightCurrent;
    this.client.audioController.playById(SfxId.Craft);
    for (const cb of this.changedSubscribers) cb();

    const name = this.client.getEifRecordById(craftItemId)?.name ?? '';
    const msg = this.client.locale.shop.craftedMsg.replace('{name}', name);
    this.client.toastController.showSuccess(msg);
    this.client.chatController.notifyServerChat({
      message: msg,
      icon: ChatIcon.Trophy,
    });
  }

  buyItem(itemId: number, maxAmount: number): void {
    if (maxAmount <= 0) return;

    const send = (amount: number) => {
      if (amount <= 0 || amount > maxAmount) return;
      this.sendBuyItem(itemId, amount);
    };

    if (maxAmount > 1) {
      const title = this.client.getResourceString(
        EOResourceID.DIALOG_TRANSFER_HOW_MUCH,
      );
      this.client.alertController.showAmount(
        title,
        this.getItemName(itemId),
        maxAmount,
        this.client.locale.shop.buyAction,
        (amount) => {
          if (amount !== null) send(amount);
        },
        this.client.locale.shop.buyAgainAction,
      );
      return;
    }

    send(1);
  }

  sellItem(itemId: number, maxAmount: number): void {
    if (maxAmount <= 0) return;

    const send = (amount: number) => {
      if (amount <= 0 || amount > maxAmount) return;
      this.sendSellItem(itemId, amount);
    };

    if (maxAmount > 1) {
      const title = this.client.getResourceString(
        EOResourceID.DIALOG_TRANSFER_HOW_MUCH,
      );
      this.client.alertController.showAmount(
        title,
        this.getItemName(itemId),
        maxAmount,
        this.client.locale.shop.sellAction,
        (amount) => {
          if (amount !== null) send(amount);
        },
      );
      return;
    }

    send(1);
  }

  sellAllItem(itemId: number): void {
    const amount = this.client.inventoryController.getItemAmount(itemId);
    if (amount <= 0) return;
    this.sendSellItem(itemId, amount);
  }

  canCraft(item: ShopCraftItem): boolean {
    for (const ingredient of item.ingredients) {
      if (!ingredient.id || ingredient.amount <= 0) continue;
      if (
        this.client.inventoryController.getItemAmount(ingredient.id) <
        ingredient.amount
      ) {
        return false;
      }
    }
    return true;
  }

  craftItem(itemId: number): void {
    const packet = new ShopCreateClientPacket();
    packet.sessionId = this.client.sessionId;
    packet.craftItemId = itemId;
    this.client.bus!.send(packet);
  }

  private sendBuyItem(itemId: number, amount: number): void {
    const packet = new ShopBuyClientPacket();
    packet.sessionId = this.client.sessionId;
    packet.buyItem = new Item();
    packet.buyItem.id = itemId;
    packet.buyItem.amount = amount;
    this.client.bus!.send(packet);
  }

  private sendSellItem(itemId: number, amount: number): void {
    const packet = new ShopSellClientPacket();
    packet.sessionId = this.client.sessionId;
    packet.sellItem = new Item();
    packet.sellItem.id = itemId;
    packet.sellItem.amount = amount;
    this.client.bus!.send(packet);
  }

  private getItemName(itemId: number): string {
    const record = this.client.getEifRecordById(itemId);
    if (record?.name) return record.name;
    return this.client.locale.shared.itemFallbackName.replace(
      '{id}',
      String(itemId),
    );
  }
}
